import { AuthenticatedUserContext, CrfFormByCodesRoute, FormMapping, IMenuItem, InstitutionsByCodesRoute, OnlinePatientManagementContentProvider, OnlinePatientManagementContext, PatientDetailsByStudyNumberRoute, RouteLoading, useIsMobile } from "@ngt/opms";
import { ReactElement, useContext, useMemo } from "react";
import { UserType } from "./api/dtos";

import { faEyeSlash, faNotesMedical, faUpload, faUser } from '@fortawesome/pro-duotone-svg-icons';
import { faFileMedical as saeIcon } from '@fortawesome/pro-duotone-svg-icons/faFileMedical';
import { faCopyright } from '@fortawesome/pro-regular-svg-icons/faCopyright';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from "@mui/material";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { SaeDetailsRoute, SaesAndQueriesRoute } from "@ngt/opms-sae";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppSettings } from "./AppSettings";
import LanguageSelect from "./components/controls/LanguageSelect";
import ToggleFeedbackButton from "./components/buttons/ToggleFeedbackButton";
import AdditionalCancerForm from "./components/forms/crf/AdditionalCancerForm";
import BreastMRIForm from "./components/forms/crf/BreastMRIForm";
import ContrastEnhancedMammogramForm from "./components/forms/crf/ContrastEnhancedMammogramForm";
import DiagnosticImagingForm from "./components/forms/crf/DiagnosticImagingForm";
import GeneralConsentForm from "./components/forms/crf/GeneralConsentForm";
import HistopathologyForm from "./components/forms/crf/HistopathologyForm";
import ImageReportUploadForm from "./components/forms/crf/ImageReportUploadForm";
import MainConsent from "./components/forms/crf/MainConsent";
import MedicalHistoryForm from "./components/forms/crf/MedicalHistoryForm";
import Procomida from './components/forms/crf/Procomida';
import PROMsForm from "./components/forms/crf/PROMsForm";
import RadiologyReportForm from "./components/forms/crf/RadiologyReportForm";
import RegistrationEligibilityChecklistForm from "./components/forms/crf/RegistrationEligibilityChecklistForm";
import ScreeningForm from "./components/forms/crf/ScreeningForm";
import ServicesAustraliaConsent from "./components/forms/crf/ServicesAustraliaConsent";
import SurgeryForm from "./components/forms/crf/SurgeryForm";
import TissueBankingConsent from "./components/forms/crf/TissueBankingConsent";
import TissueCollectionForm from "./components/forms/crf/TissueCollectionForm";
import TranslationalBloodCollectionForm from "./components/forms/crf/TranslationalBloodCollectionForm";
import TreatmentDecisionsForm from "./components/forms/crf/TreatmentDecisionsForm";
import BreastCancerTreatmentOutcomesScaleForm from "./components/forms/pros/BreastCancerTreatmentOutcomesScaleForm";
import BriefIllnessPerceptionQuestionnaireForm from "./components/forms/pros/BriefIllnessPerceptionQuestionnaireForm";
import DecisionRegretScaleForm from "./components/forms/pros/DecisionRegretScaleForm";
import EndocrineTherapyAdherenceForm from "./components/forms/pros/EndocrineTherapyAdherenceForm";
import EORTCIL353Form from "./components/forms/pros/EORTCIL353Form";
import Eq5d5lForm from "./components/forms/pros/Eq5d5lForm";
import ExperienceAndImpactOfTreatmentForm from './components/forms/pros/ExperienceAndImpactOfTreatmentForm';
import FearOfCancerRecurrenceForm from "./components/forms/pros/FearOfCancerRecurrenceForm";
import GeneralizedAnxietyDisorder2Form from "./components/forms/pros/GeneralizedAnxietyDisorder2Form";
import InternationalPersonalityItemPoolForm from "./components/forms/pros/InternationalPersonalityItemPoolForm";
import PatientHealthQuestionnaire2Form from './components/forms/pros/PatientHealthQuestionnaire2Form';
import RiskAppraisalForm from "./components/forms/pros/RiskAppraisalForm";
import SystemErrorForm from "./components/forms/staff/SystemErrorForm";
import { PdfViewerPage } from "./components/page/pdfviewer/PdfViewer";
import EConsentByGuidRoute from "./components/routes/form/EConsentByGuidRoute";
import ProFormByCodesRoute from "./components/routes/form/ProFormByCodesRoute";
import useSentryUser from "./hooks/sentry/useSentryUser";
import ErrorPage, { ErrorTypeEnum } from "./pages/ErrorPage";
import ImageUploadPage from "./pages/ImageUpload/ImageUploadPage";
import Locales from "./pages/locale/Locales";
import PatientEConsentPage from "./pages/PatientEConsentPage";
import PatientProsPage from "./pages/PatientProsPage";
import PatientSummaryPage from "./pages/PatientSummaryPage";
import RegistrationPage from "./pages/RegistrationPage";
import Reports from "./pages/Reports";
import SaeNotifyPage from "./pages/sae/SaeNotifyPage";
import SaesPage from "./pages/sae/SaesPage";
import SaeViewPage from "./pages/sae/SaeViewPage";
import TrialInformationPage from "./pages/TrialInformationPage";
import PatientLanguageSetterWrapper from "./components/controls/PatientLanguageSetterWrapper";
import WithdrawalOfConsent from "./components/forms/crf/WithdrawalOfConsent";

const formMapping: Record<string, ReactElement> = {
    "general-demographic": <GeneralConsentForm />,
    "registration-eligibility-checklist": <RegistrationEligibilityChecklistForm />,
    "diagnostic-imaging-and-histopathology": <DiagnosticImagingForm />,
    "medical-history": <MedicalHistoryForm />,
    "proms": <PROMsForm />,
    "breast-mri": <BreastMRIForm />,
    "contrast-enhanced-mammogram": <ContrastEnhancedMammogramForm />,
    "translational-blood-collection": <TranslationalBloodCollectionForm />,
    "surgery": <SurgeryForm />,
    "histopathology": <HistopathologyForm />,
    "additional-cancer": <AdditionalCancerForm />,
    "radiology-report": <RadiologyReportForm />,
    "treatment-decisions": <TreatmentDecisionsForm />,
    "tissue-collection": <TissueCollectionForm />,
    "screening-form": <ScreeningForm />,
    "main-consent": <MainConsent />,
    "tissue-banking-consent": <TissueBankingConsent />,
    "services-australia-consent": <ServicesAustraliaConsent />,
    "image-report-upload": <ImageReportUploadForm />,
    "risk-appraisal": <RiskAppraisalForm />,
    "patient-health-questionnaire-2": <PatientHealthQuestionnaire2Form />,
    "international-personality-item-pool": <InternationalPersonalityItemPoolForm />,
    "generalized-anxiety-disorder-2": <GeneralizedAnxietyDisorder2Form />,
    "fear-of-cancer-recurrence": <FearOfCancerRecurrenceForm />,
    "endocrine-therapy-adherence": <EndocrineTherapyAdherenceForm />,
    "experience-and-impact-of-treatement": <ExperienceAndImpactOfTreatmentForm />,
    "eq-5d-5l": <Eq5d5lForm />,
    "eortc-il353": <EORTCIL353Form />,
    "decision-regret-scale": <DecisionRegretScaleForm />,
    "brief-illness-perception-questionnaire": <BriefIllnessPerceptionQuestionnaireForm />,
    "breast-cancer-treatment-outcomes-scale": <BreastCancerTreatmentOutcomesScaleForm />,
    "procomida": <Procomida />,
    "system-error-form": <SystemErrorForm />,
    "withdrawal-of-consent": <WithdrawalOfConsent />
}

const App = () => {
    useSentryUser();
    
    const { data: user } = useContext(AuthenticatedUserContext);
    const { routeParameters } = useContext(OnlinePatientManagementContext);

    const isPatient = user?.type === UserType.Patient;
    
    const isMobile = useIsMobile();

    const {
        permissions: [
            canAccessOpms, 
            canAdministerOpms,
            canViewSae,
            canViewDrugManagement, 
            canCentralReview,
            isSiteUser
            //canAdministerDrugManagement, 
            //canDispenseDrug
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAccess,
            Permission.OpmsAdminister,
            Permission.SaeView,
            Permission.DrugManagementView,
            Permission.CanCentralReview,
            Permission.IsSiteUser
            //Permission.DrugManagementAdminister,
            //Permission.DrugManagementDispenseDrug
        ]
    );

    // useEffect(() => {i18n.changeLanguage("ko")}, []);

    const isCentralReviewer = useMemo(() => {
        return canCentralReview && !canAdministerOpms;
    }, [canAdministerOpms, canCentralReview]);

    const menuItems: IMenuItem[] = useMemo(() => {
        if (isPatient || !user) {
            return [];
        }

        let items: IMenuItem[] = [];

        // if ((canViewEConsent && eConsentSettings.eConsentActivated) || canViewScreeningLog) {
        //     items.push({
        //         url: '/econsent',
        //         label: 'Participant Information Portal',
        //         external: false,
        //         newTab: false,
        //         icon: eConsentIcon as any
        //     });
        // }
        if(!isCentralReviewer) {
            items.push(
                {
                    url: '/registration',
                    label: 'Participants',
                    external: false,
                    newTab: false,
                    icon: faUser as any
                }
            );

            if (canViewSae) {
                items.push({
                    url: '/sae',
                    label: 'SAE Review',
                    external: false,
                    newTab: false,
                    icon: saeIcon as any
                });
            }
        }
        
        ///////////////////////////////////
        // BCTPRSPTVE-62 - TURN OFF DICOM
        ///////////////////////////////////
        if (AppSettings.enableDicom && (canCentralReview || canAdministerOpms || isSiteUser)) {
            items.push(
                {
                    url: '/image-upload',
                    label: 'Image Upload',
                    external: false,
                    newTab: false,
                    icon: faUpload as any
                }
            );
        }
        // if (canViewDrugManagement) {
        //     items.push({
        //         url: drugManagementBaseUrl,
        //         label: 'Drug Management',
        //         external: false,
        //         newTab: false,
        //         icon: faFirstAid as any
        //     });
        // }
        if (canAdministerOpms) {
            items.push(
                {
                    url: '/reports',
                    label: 'Reports',
                    external: false,
                    newTab: false,
                    icon: faUser as any
                }
            )
        }

        if(!isCentralReviewer) {
            items.push(
                {
                    url: AppSettings.ecrfUrl,
                    label: 'eCRF',
                    external: true,
                    newTab: true,
                    icon: faNotesMedical as any
                }
            );

            items.push(
                {
                    url: AppSettings.redactUrl,
                    label: 'Redact',
                    external: true,
                    newTab: true,
                    icon: faEyeSlash as any
                }
            );
        }

        return items;
    }, [canAdministerOpms, canCentralReview, canViewDrugManagement, canViewSae, isCentralReviewer, isPatient, isSiteUser, user]);

    const invalidPermission = user && !permissionsLoading && !canAccessOpms;
    
    const additionalHeaderComponents = useMemo(() => {
        return (
            AppSettings.sentry.dsn && 
            AppSettings.sentry.enableReportABug ?
                [<ToggleFeedbackButton />] :
                undefined
        );
    }, []);

    if (permissionsLoading && user) {
        return (
            <RouteLoading />
        );
    }

    if (invalidPermission) {
        return (
            <OnlinePatientManagementContentProvider
                menuItems={menuItems}
                footer={
                    isMobile ?
                        undefined :
                        <Typography variant="caption">
                            Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                        </Typography>
                }
                additionalHeaderComponents={additionalHeaderComponents}
            >
                <ErrorPage errorType={ErrorTypeEnum.e403} />
            </OnlinePatientManagementContentProvider>
        );
    }

    if (!user) { 
        return (
            <Routes>
                <Route 
                    path="/" 
                    element={
                        <OnlinePatientManagementContentProvider
                            menuItems={menuItems}
                            footer={
                                isMobile ?
                                    undefined :
                                    <Typography variant="caption">
                                        Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                    </Typography>
                            }
                            additionalHeaderComponents={additionalHeaderComponents}
                        >
                            <Outlet />
                        </OnlinePatientManagementContentProvider>
                    }
                >
                    <Route path='/trial-consent'>
                        <Route index element={<TrialInformationPage />} />
                        <Route path='participant/information'>
                            <Route index element={<TrialInformationPage />} />
                            <Route path=":instCode" element={<TrialInformationPage />} />
                        </Route>
                        <Route 
                            path=":userType/econsent/:guid" 
                            element={
                                <EConsentByGuidRoute
                                    loadBeforeMount
                                >
                                    <Outlet />
                                </EConsentByGuidRoute>
                            }
                        >
                            <Route index element={<PatientEConsentPage />} />
                            <Route path={`:${routeParameters.formDefinitionCode}`} element={<FormMapping mapping={formMapping} />} />
                        </Route> 
                        <Route path="*" element={<Navigate to="/trial-consent" replace />} />   
                    </Route>
                    <Route path="*" element={<Navigate to="/trial-consent" replace />} />            
                </Route>
            </Routes>
        )
    }

    if (isPatient) { 
        return (
            <Routes>
                <Route 
                    path="/" 
                    element={
                        <OnlinePatientManagementContentProvider
                            menuItems={menuItems}
                            footer={
                                isMobile ?
                                    undefined :
                                    <Typography variant="caption">
                                        Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                    </Typography>
                            }
                            additionalHeaderComponents={AppSettings.enableTranslation ? [<LanguageSelect />] : undefined}
                        >
                            <PatientDetailsByStudyNumberRoute 
                                patientStudyNumber={user?.userName}
                                loadBeforeMount
                            >
                                <PatientLanguageSetterWrapper>
                                    <Outlet />
                                </PatientLanguageSetterWrapper>
                            </PatientDetailsByStudyNumberRoute>
                        </OnlinePatientManagementContentProvider>
                    }
                >
                    <Route index element={<PatientProsPage />} />
                    <Route path=":next" element={<PatientProsPage />} />
                    <Route 
                        path={`:${routeParameters.eventDefinitionCode}/:${routeParameters.eventRepeat}/:${routeParameters.formDefinitionCode}/:${routeParameters.formRepeat}`}
                        element={
                            <ProFormByCodesRoute
                                loadBeforeMount
                                validateBeforeMount={{
                                    form: true
                                }}
                                createEvent
                                createForm
                            >
                                <FormMapping mapping={formMapping} />
                            </ProFormByCodesRoute>
                        }
                    />            
                </Route>
                <Route path="error/403" element={<ErrorPage errorType={ErrorTypeEnum.e403} />} />
                <Route path="error/404" element={<ErrorPage errorType={ErrorTypeEnum.e404} />} />
                <Route path="error/405" element={<ErrorPage errorType={ErrorTypeEnum.e405} />} />
                <Route path="error/500" element={<ErrorPage errorType={ErrorTypeEnum.e500} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        )
    }

    if (isCentralReviewer)
    {
        return (
            <Routes>
                <Route 
                    path="/" 
                    element={
                        <OnlinePatientManagementContentProvider
                            menuItems={menuItems}
                            footer={
                                isMobile ?
                                    undefined :
                                    <Typography variant="caption">
                                        Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                    </Typography>
                            }
                            additionalHeaderComponents={additionalHeaderComponents}
                        >
                            <Outlet />
                        </OnlinePatientManagementContentProvider>
                    }
                >
                    {
                        AppSettings.enableDicom &&
                        <>
                            <Route 
                                path="image-upload" 
                                element={
                                    <InstitutionsByCodesRoute 
                                        loadBeforeMount 
                                    >
                                        <Outlet />
                                    </InstitutionsByCodesRoute>
                                }>
                                <Route index element={<ImageUploadPage />} />
                                <Route path={`:${routeParameters.institutionCode}`}>
                                    <Route index element={<ImageUploadPage />} />
                                </Route>

                            </Route>
                            <Route index element={<Navigate to="/image-upload" replace />} />
                            <Route path="*" element={<Navigate to="/image-upload" replace />} />
                        </>
                    }
                </Route>
            </Routes>

        );
    }

    return (
        <Routes>
            <Route 
                path="/" 
                element={
                    <OnlinePatientManagementContentProvider
                        menuItems={menuItems}
                        footer={
                            isMobile ?
                                undefined :
                                <Typography variant="caption">
                                    Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                </Typography>
                        }
                        additionalHeaderComponents={additionalHeaderComponents}
                    >
                        <Outlet />
                    </OnlinePatientManagementContentProvider>
                }
            >
                {
                    canAdministerOpms &&
                    <Route
                        path="admin"
                        element={<Outlet />}
                    >
                        <Route
                            path="manage-locales"
                            element={<Outlet />}
                        >
                            <Route
                                index
                                element={<Locales />}
                            />
                        </Route>
                    </Route>
                }
                <Route 
                    path="registration" 
                    element={
                        <InstitutionsByCodesRoute 
                            loadBeforeMount 
                        >
                            <Outlet />
                        </InstitutionsByCodesRoute>
                    }>
                    <Route 
                        index 
                        element={
                            <RegistrationPage 
                                isBct={canAdministerOpms}
                            />
                        } 
                    />
                    <Route path={`:${routeParameters.institutionCode}`}>
                        <Route 
                            index 
                            element={
                                <RegistrationPage 
                                    isBct={canAdministerOpms}
                                />
                            } 
                        />
                        <Route 
                            path={`:${routeParameters.patientStudyNumber}`} 
                            element={
                                <PatientDetailsByStudyNumberRoute 
                                    loadBeforeMount
                                >
                                    <Outlet />
                                </PatientDetailsByStudyNumberRoute>
                            }
                        >
                            <Route index element={<PatientSummaryPage />} />
                            <Route 
                                path={`:${routeParameters.eventDefinitionCode}/:${routeParameters.eventRepeat}/:${routeParameters.formDefinitionCode}/:${routeParameters.formRepeat}`}
                                element={
                                    <CrfFormByCodesRoute
                                        loadBeforeMount
                                        validateBeforeMount={{
                                            form: true
                                        }}
                                        createEvent
                                        createForm
                                    >
                                        <Outlet />
                                    </CrfFormByCodesRoute>
                                }
                            >
                                <Route index element={<FormMapping mapping={formMapping} />} />
                                <Route path=":override" element={<FormMapping mapping={formMapping} />} />
                            </Route>
                        </Route>
                    </Route>
                </Route>
                <Route
                    path={`/sae/:${routeParameters.institutionCode}/:${routeParameters.patientStudyNumber}/:saeNumber`}
                    element={
                        <PatientDetailsByStudyNumberRoute 
                            loadBeforeMount
                        >
                            <SaeDetailsRoute
                                loadBeforeMount
                            >
                                <Outlet />
                            </SaeDetailsRoute>
                        </PatientDetailsByStudyNumberRoute>
                    }
                >
                    <Route index element={<SaeViewPage />} />
                    <Route path="notify/:notifyMode" element={<SaeNotifyPage />} />
                    <Route path=":viewMode" element={<SaeViewPage />} />
                </Route>
                <Route 
                    path="/sae" 
                    element={
                        <SaesAndQueriesRoute
                            loadBeforeMount
                        >
                            <Outlet />
                        </SaesAndQueriesRoute>
                    }
                >
                    <Route index element={<SaesPage />} />
                    <Route path={`:${routeParameters.institutionCode}`} element={<SaesPage />} />
                </Route>


                {
                /**
                 * ///////////////////////////////////
                 * // BCTPRSPTVE-62 - TURN OFF DICOM
                 * ///////////////////////////////////
                 */
                }
                {
                    AppSettings.enableDicom &&
                    <Route 
                        path="image-upload" 
                        element={
                            <InstitutionsByCodesRoute 
                                loadBeforeMount 
                            >
                                <Outlet />
                            </InstitutionsByCodesRoute>
                        }>
                        <Route index element={<ImageUploadPage />} />
                        <Route path={`:${routeParameters.institutionCode}`}>
                            <Route index element={<ImageUploadPage />} />
                        </Route>
                    </Route>
                }

                <Route path='/trial-consent'>
                    <Route index element={<TrialInformationPage />} />
                    <Route path='participant/information'>
                            <Route index element={<TrialInformationPage />} />
                            <Route path=":instCode" element={<TrialInformationPage />} />
                        </Route>
                    <Route 
                        path=":userType/econsent/:guid" 
                        element={
                            <EConsentByGuidRoute
                                loadBeforeMount
                            >
                                <Outlet />
                            </EConsentByGuidRoute>
                        }
                    >
                        <Route index element={<PatientEConsentPage />} />
                        <Route path={`:${routeParameters.formDefinitionCode}`} element={<FormMapping mapping={formMapping} />} />
                    </Route> 
                    <Route path="*" element={<Navigate to="/trial-consent" replace />} />   
                </Route>

                <Route path='/reports'>
                    <Route index element={<Reports />} />
                    <Route path="*" element={<Navigate to="/reports" replace />} />   
                </Route>

                <Route path="error/403" element={<ErrorPage errorType={ErrorTypeEnum.e403} />} />
                <Route path="error/404" element={<ErrorPage errorType={ErrorTypeEnum.e404} />} />
                <Route path="error/405" element={<ErrorPage errorType={ErrorTypeEnum.e405} />} />
                <Route path="error/500" element={<ErrorPage errorType={ErrorTypeEnum.e500} />} />
                <Route index element={<Navigate to="/registration" replace />} />
                <Route path="*" element={<Navigate to="/registration" replace />} />
            </Route>
            <Route
                path="pdfviewer"
            >
                <Route path="file/:fileId" element={<PdfViewerPage />} />
                <Route path="file-upload/:guid" element={<PdfViewerPage />} />
            </Route>
        </Routes>
    );
}

export default App
